/**
 * GlobalSoftphone — persistent floating widget in the BDC portal.
 *
 * Phase 11 updates:
 *   • Draggable via custom pointer-events hook (react-draggable v4 is broken
 *     under React 19 — its build still calls the removed ReactDOM.findDOMNode).
 *     Position persisted in localStorage.
 *   • Drag handle at the top; double-click resets to bottom-right
 *   • Retry button when status = error (Twilio Access Token failures,
 *     mic-permission blocks, transient net drops)
 *   • Sanitized error copy — never surfaces raw Twilio SDK text
 *
 * States:
 *   • idle (offline / ready): compact pill, presence toggle
 *   • incoming: expands to accept/reject banner
 *   • active call: expands to full dialer with mute + DTMF + hangup
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Phone, PhoneCall, PhoneIncoming, PhoneOff, Mic, MicOff, Delete,
  Loader2, Wifi, WifiOff, AlertTriangle, GripHorizontal, RefreshCw,
} from "lucide-react";
import { useSoftphone, SOFTPHONE_STATUS } from "@/context/SoftphoneContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPhoneDisplay } from "@/lib/phone";

const STORAGE_KEY = "belomotors_softphone_position";
const WIDGET_W = 300;
const WIDGET_H = 200; // rough estimate; only used to keep widget on-screen

const STATUS_STYLE = {
  offline:    { label: "Offline",     icon: WifiOff,        dot: "bg-slate-400",              text: "text-slate-300" },
  connecting: { label: "Connecting…", icon: Loader2,        dot: "bg-amber-400 animate-pulse", text: "text-amber-300", spin: true },
  ready:      { label: "Ready",       icon: Wifi,           dot: "bg-emerald-400",            text: "text-emerald-300" },
  busy:       { label: "On call",     icon: PhoneCall,      dot: "bg-[#D4A537] animate-pulse", text: "text-[#D4A537]" },
  error:      { label: "Error",       icon: AlertTriangle,  dot: "bg-rose-500",               text: "text-rose-400" },
};

// Compute the default anchor (bottom-right corner with 16px inset).
const defaultAnchor = () => {
  const w = typeof window !== "undefined" ? window.innerWidth : 1200;
  const h = typeof window !== "undefined" ? window.innerHeight : 800;
  return { x: w - WIDGET_W - 16, y: h - WIDGET_H - 16 };
};

const loadStoredPosition = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return defaultAnchor();
    const p = JSON.parse(raw);
    if (typeof p?.x !== "number" || typeof p?.y !== "number") return defaultAnchor();
    // Clamp to viewport if the window resized since last save.
    const w = window.innerWidth;
    const h = window.innerHeight;
    return {
      x: Math.min(Math.max(p.x, 0), Math.max(0, w - WIDGET_W)),
      y: Math.min(Math.max(p.y, 0), Math.max(0, h - WIDGET_H)),
    };
  } catch {
    return defaultAnchor();
  }
};

const useCallTimer = (activeCall) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!activeCall) {
      setElapsed(0);
      return;
    }
    const started = new Date(activeCall.startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [activeCall]);
  const mm = Math.floor(elapsed / 60);
  const ss = elapsed % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const Dot = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.offline;
  return <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} aria-hidden />;
};

const StatusLine = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.offline;
  const Icon = s.icon;
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="softphone-status">
      <Icon size={12} className={`${s.text} ${s.spin ? "animate-spin" : ""}`} />
      <span className={s.text}>{s.label}</span>
    </div>
  );
};

const DragHandle = ({ onDoubleClick, handleProps }) => (
  <div
    className="softphone-drag-handle flex items-center justify-center gap-1 cursor-move py-1 -mt-1 mb-1 text-white/40 hover:text-white/80 transition-colors select-none"
    onDoubleClick={onDoubleClick}
    title="Drag to move · double-click to reset"
    data-testid="softphone-drag-handle"
    {...(handleProps || {})}
  >
    <GripHorizontal size={14} />
    <span className="text-[9px] uppercase tracking-widest">drag</span>
  </div>
);

const IncomingBanner = ({ from, onAccept, onReject, onDoubleClickReset, handleProps }) => (
  <div
    className="rounded-2xl bg-gradient-to-br from-[#1B3A6B] to-[#0f2447] text-white shadow-2xl border border-[#D4A537]/40 p-4 w-72"
    data-testid="softphone-incoming"
  >
    <DragHandle onDoubleClick={onDoubleClickReset} handleProps={handleProps} />
    <div className="flex items-center gap-2 text-[#D4A537] mb-2">
      <PhoneIncoming size={16} className="animate-pulse" />
      <p className="text-xs uppercase tracking-widest font-semibold">Incoming call</p>
    </div>
    <p className="font-display text-lg font-semibold tracking-tight">
      {formatPhoneDisplay(from) || "Unknown caller"}
    </p>
    <div className="mt-4 flex items-center gap-2">
      <Button onClick={onAccept} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold" data-testid="softphone-accept-btn">
        <Phone size={14} /> Accept
      </Button>
      <Button onClick={onReject} variant="ghost" className="flex-1 text-white/80 hover:text-white hover:bg-white/10" data-testid="softphone-reject-btn">
        <PhoneOff size={14} /> Dismiss
      </Button>
    </div>
  </div>
);

const ActiveCallPanel = ({ activeCall, muted, onHangup, onToggleMute, onSendDtmf, elapsed, onDoubleClickReset, handleProps }) => {
  const [padOpen, setPadOpen] = useState(false);
  const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
  const peer = activeCall?.direction === "outbound" ? activeCall?.to : activeCall?.from;
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#1B3A6B] to-[#0f2447] text-white shadow-2xl border border-[#D4A537]/40 p-4 w-72"
      data-testid="softphone-active"
    >
      <DragHandle onDoubleClick={onDoubleClickReset} handleProps={handleProps} />
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-[#D4A537]">
          <PhoneCall size={14} className="animate-pulse" />
          <p className="text-[10px] uppercase tracking-widest font-semibold">
            {activeCall?.direction === "outbound" ? "Outbound call" : "Inbound call"}
          </p>
        </div>
        <span className="text-[11px] font-mono text-white/70" data-testid="softphone-elapsed">{elapsed}</span>
      </div>
      <p className="font-display text-lg font-semibold tracking-tight truncate">
        {formatPhoneDisplay(peer) || "Connected"}
      </p>
      {padOpen && (
        <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="softphone-dtmf-pad">
          {digits.map((d) => (
            <button key={d} onClick={() => onSendDtmf(d)} className="rounded-lg bg-white/10 hover:bg-white/20 text-white font-mono font-semibold py-2 text-sm" data-testid={`softphone-dtmf-${d}`}>
              {d}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <Button onClick={onToggleMute} variant="ghost" className={`text-white hover:bg-white/10 ${muted ? "bg-rose-500/30" : ""}`} data-testid="softphone-mute-btn">
          {muted ? <MicOff size={16} /> : <Mic size={16} />}
        </Button>
        <Button onClick={() => setPadOpen((v) => !v)} variant="ghost" className="text-white hover:bg-white/10" data-testid="softphone-dtmf-toggle">
          <Delete size={16} />
        </Button>
        <Button onClick={onHangup} className="bg-rose-500 hover:bg-rose-400 text-white font-semibold" data-testid="softphone-hangup-btn">
          <PhoneOff size={16} />
        </Button>
      </div>
    </div>
  );
};

const CompactBar = ({ status, error, onGoOnline, onGoOffline, onRetry, onDial, twilioNumber, onDoubleClickReset, handleProps }) => {
  const [dialOpen, setDialOpen] = useState(false);
  const [num, setNum] = useState("");

  const isReady = status === SOFTPHONE_STATUS.READY;
  const isConnecting = status === SOFTPHONE_STATUS.CONNECTING;
  const isOffline = status === SOFTPHONE_STATUS.OFFLINE;
  const isError = status === SOFTPHONE_STATUS.ERROR;

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#1B3A6B] to-[#0f2447] text-white shadow-2xl border border-white/10 p-3 w-72"
      data-testid="softphone-widget"
    >
      <DragHandle onDoubleClick={onDoubleClickReset} handleProps={handleProps} />
      <div className="flex items-center gap-2 mb-2">
        <Dot status={status} />
        <p className="font-display text-sm font-semibold flex-1">
          Softphone
          {twilioNumber && (
            <span className="ml-2 font-mono text-[10px] text-white/60">
              {formatPhoneDisplay(twilioNumber)}
            </span>
          )}
        </p>
        {isOffline && (
          <Button size="sm" className="h-7 bg-[#D4A537] hover:bg-[#c0942f] text-slate-900 font-semibold text-xs" onClick={onGoOnline} data-testid="softphone-go-online-btn">
            Go online
          </Button>
        )}
        {isError && (
          <Button size="sm" className="h-7 bg-rose-500 hover:bg-rose-400 text-white font-semibold text-xs" onClick={onRetry} data-testid="softphone-retry-btn">
            <RefreshCw size={12} /> Retry
          </Button>
        )}
        {(isReady || status === SOFTPHONE_STATUS.BUSY) && (
          <Button size="sm" variant="ghost" className="h-7 text-white/70 hover:text-white hover:bg-white/10 text-xs" onClick={onGoOffline} data-testid="softphone-go-offline-btn">
            Offline
          </Button>
        )}
      </div>

      <StatusLine status={status} />
      {error && isError && (
        <p className="mt-1 text-[10px] text-rose-300 leading-snug" data-testid="softphone-error-msg">{error}</p>
      )}

      {isReady && (
        <div className="mt-3">
          {dialOpen ? (
            <div className="space-y-2" data-testid="softphone-dialer">
              <Input autoFocus value={num} onChange={(e) => setNum(e.target.value)} placeholder="+16175551234" className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-8 text-sm" data-testid="softphone-dial-input" />
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-white text-xs" onClick={() => { if (!num.trim()) return; onDial(num.trim()); setDialOpen(false); }} data-testid="softphone-dial-confirm-btn">
                  <PhoneCall size={12} /> Call
                </Button>
                <Button size="sm" variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 text-xs" onClick={() => { setDialOpen(false); setNum(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" className="w-full mt-1 bg-white/10 hover:bg-white/20 text-white text-xs" onClick={() => setDialOpen(true)} data-testid="softphone-open-dialer-btn">
              <Phone size={12} /> Quick dial
            </Button>
          )}
        </div>
      )}
      {isConnecting && (
        <p className="mt-2 text-[11px] text-white/60">Requesting microphone…</p>
      )}
    </div>
  );
};

const useDraggable = (storageKey, defaultAnchorFn, widgetW, widgetH) => {
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return defaultAnchorFn();
      const p = JSON.parse(raw);
      if (typeof p?.x !== "number" || typeof p?.y !== "number") return defaultAnchorFn();
      const w = window.innerWidth; const h = window.innerHeight;
      return {
        x: Math.min(Math.max(p.x, 0), Math.max(0, w - widgetW)),
        y: Math.min(Math.max(p.y, 0), Math.max(0, h - widgetH)),
      };
    } catch { return defaultAnchorFn(); }
  });
  const dragRef = useRef(null); // { startX, startY, origX, origY }

  const onPointerDown = useCallback((e) => {
    // Ignore right-click and modifier drags; buttons other than "0" (main).
    if (e.button !== 0) return;
    // Only start dragging when the target is (or is inside) the handle.
    const handle = e.currentTarget.closest?.(".softphone-drag-handle") || e.currentTarget;
    if (!handle) return;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: position.x, origY: position.y,
      pointerId: e.pointerId,
    };
    try { handle.setPointerCapture(e.pointerId); } catch { /* pointer capture unsupported — non-fatal */ }
    e.preventDefault();
  }, [position.x, position.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const w = window.innerWidth; const h = window.innerHeight;
    setPosition({
      x: Math.min(Math.max(dragRef.current.origX + dx, 0), Math.max(0, w - widgetW)),
      y: Math.min(Math.max(dragRef.current.origY + dy, 0), Math.max(0, h - widgetH)),
    });
  }, [widgetW, widgetH]);

  const onPointerUp = useCallback((e) => {
    if (!dragRef.current) return;
    try { e.currentTarget.releasePointerCapture?.(dragRef.current.pointerId); } catch { /* noop */ }
    dragRef.current = null;
    // Persist the final position.
    setPosition((cur) => {
      try { window.localStorage.setItem(storageKey, JSON.stringify(cur)); } catch { /* storage quota / private mode */ }
      return cur;
    });
  }, [storageKey]);

  const reset = useCallback(() => {
    const next = defaultAnchorFn();
    setPosition(next);
    try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* noop */ }
  }, [defaultAnchorFn, storageKey]);

  // Re-clamp on window resize so the widget never floats off-screen.
  useEffect(() => {
    const onResize = () => setPosition((p) => {
      const w = window.innerWidth; const h = window.innerHeight;
      return {
        x: Math.min(Math.max(p.x, 0), Math.max(0, w - widgetW)),
        y: Math.min(Math.max(p.y, 0), Math.max(0, h - widgetH)),
      };
    });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [widgetW, widgetH]);

  return { position, reset, handleProps: { onPointerDown, onPointerMove, onPointerUp } };
};

const GlobalSoftphone = () => {
  const {
    status, error, activeCall, incoming, muted, twilioNumber,
    goOnline, goOffline, retry, dial, hangup, toggleMute, sendDtmf,
    acceptIncoming, rejectIncoming,
  } = useSoftphone();

  const elapsed = useCallTimer(activeCall);
  const incomingFrom = useMemo(() => incoming?.parameters?.From, [incoming]);
  const { position, reset, handleProps } = useDraggable(STORAGE_KEY, defaultAnchor, WIDGET_W, WIDGET_H);

  return (
    <div
      className="fixed z-50"
      style={{ left: position.x, top: position.y, touchAction: "none" }}
      data-testid="global-softphone"
    >
      {incoming && !activeCall && (
        <IncomingBanner
          from={incomingFrom}
          onAccept={acceptIncoming}
          onReject={rejectIncoming}
          onDoubleClickReset={reset}
          handleProps={handleProps}
        />
      )}
      {activeCall && (
        <ActiveCallPanel
          activeCall={activeCall}
          muted={muted}
          onHangup={hangup}
          onToggleMute={toggleMute}
          onSendDtmf={sendDtmf}
          elapsed={elapsed}
          onDoubleClickReset={reset}
          handleProps={handleProps}
        />
      )}
      {!activeCall && !incoming && (
        <CompactBar
          status={status}
          error={error}
          onGoOnline={goOnline}
          onGoOffline={goOffline}
          onRetry={retry}
          onDial={dial}
          twilioNumber={twilioNumber}
          onDoubleClickReset={reset}
          handleProps={handleProps}
        />
      )}
    </div>
  );
};

export default GlobalSoftphone;
